<template>
	<div class="main">
		<header class="page-title">
			<h2>
				Holiday Editor
				<span class="page-description" v-if="currentUser.office_country !== null"><i class="fas fa-info-circle icon-left"></i> Showing national holidays for {{
						currentUser.office_country === 'hu' ? 'Hungary' : currentUser.office_country === 'uk' ? 'UK' : 'N/A'
					}}</span>
				<span class="page-description" v-if="currentUser.office_country === null"><i class="fas fa-exclamation-triangle icon-left"></i> Please set up your working location by clicking <em>Hello!</em> in the top right corner.</span>
			</h2>
			<div class="ml-auto">
				<a @click="dayClick(null)" class="btn btn-lg btn-success"><i class="fas fa-plus"></i> Reserve Your Day-Offs</a>
			</div>
		</header>
		<div class="card infobox">
			<header class="card-header">
				<h4 class="card-title">Infobox</h4>
			</header>
			<div class="card-body">
				<table>
					<thead>
					<tr>
						<th>
							<span class="th">Total Day-Offs</span>
						</th>
						<th>
							<span class="th">Remaining Day-Offs</span>
						</th>
						<th>
							<span class="th">Next Day-Off</span>
						</th>
					</tr>
					</thead>
					<tbody>
					<tr>
						<td>{{ currentUser.total_days_off }}</td>
						<td>{{ userDetails.remainingDayOffs }}</td>
						<td>{{ nextDayOff.id !== 0 ? nextDayOff.start_date + ' - ' + nextDayOff.end_date : 'N/A' }}</td>
					</tr>
					</tbody>
				</table>
			</div>
		</div>
		<div class="card pending-requests"
			 v-if="(userRoles.includes('hr.create') || userRoles.includes('users.lineManager')) && Object.keys(pendingDayOffs).length > 0">
			<header class="card-header">
				<h4 class="card-title">Pending Requests</h4>
			</header>
			<div class="card-body table-lister table-responsive">
				<table>
					<thead>
					<tr>
						<th>
							<span class="th">User</span>
						</th>
						<th>
							<span class="th">Date</span>
						</th>
						<th>
							<span class="th">Length</span>
						</th>
						<th>
							<span class="th">Actions</span>
						</th>
					</tr>
					</thead>
					<tbody>
					<tr v-for="event in pendingDayOffs">
						<td>{{ event.username }}</td>
						<td>{{ event.start_date }} - {{ event.end_date }}</td>
						<td>{{ event.length }}</td>
						<td>
							<a @click="adminEvent(event)" class="btn btn-md btn-primary"><i class="fas fa-edit"></i> Approve/Reject</a>
						</td>
					</tr>
					</tbody>
				</table>
			</div>
		</div>

		<div class="card">
			<header class="card-header">
				<h4 class="card-title">Calendar</h4>
			</header>
			<div class="card-body">
				<full-calendar :events="events" locale="en" first-day="1" @dayClick="dayClick" @eventClick="eventClick"></full-calendar>
			</div>
		</div>
		
		<div class="card was-off" v-if="userRoles.includes('users.lineManager') && Object.keys(wasOff).length > 0">
			<header class="card-header">
				<h4 class="card-title">Vacations used</h4>
			</header>
			<div class="card-body table-lister table-responsive">
				<table>
					<thead>
					<tr>
						<th>
							<span class="th">Date</span>
						</th>
						<th>
							<span class="th">Users Days off</span>
						</th>
					</tr>
					</thead>
					<tbody>
					<tr v-for="(users, index) in wasOff">
						<td>{{ index }}</td>
						<td>
							<ul>
								<li class="days-off" v-for="(user, name) in users">
									<div class="name"><i class="far fa-user icon-left"></i> <strong>{{ name }}</strong></div>
									<div class="days">
										<ul v-for="(day, date) in user.days">
											<li>{{ date }} ({{ day }})</li>
										</ul>
									</div>
									<div class="total"><strong><em>{{ user.sum }}</em></strong></div>
								</li>
							</ul>
						</td>
					</tr>
					</tbody>
				</table>
			</div>
		</div>

		<modal @close="closeModal" v-show="isModalVisible">
			<template slot="header">
				<h4 v-if="adminMode === false" class="modal-title">{{ currentEvent === null ? 'Add' : 'Edit' }} Day-Off {{
						currentEvent !== null && currentEventObject !== null && currentEventObject.isOwn !== true ? '(' + currentEventObject.username + ')' : ''
					}}</h4>
				<h4 v-if="adminMode === true && currentEventObject !== null && currentEventObject.hasOwnProperty('username')" class="modal-title">
					Admin {{ currentEventObject.username }}'s Day-Off</h4>
				<h4 v-if="adminMode === true && currentEventObject !== null && currentEventObject.hasOwnProperty('title')" class="modal-title">Admin
					{{ currentEventObject.title }}</h4>
			</template>
			<template slot="body">
				<div class="flex box">
					<div class="seven">
						<div class="flex box">
							<div class="form-group four-lg">
								<label for="start_date">Start Date</label>
								<input type="date" class="form-control" id="start_date" v-model="startDate"
									   :disabled="adminMode !== true && currentEventObject.approved">
							</div>
							<div class="form-group four-lg">
								<label for="end_date">End Date</label>
								<input type="date" class="form-control" id="end_date" v-model="endDate"
									   :disabled="adminMode !== true && currentEventObject.approved">
							</div>
							<div class="form-group four-lg">
								<label>Just a half day?</label>
								<label class="label-checkbox mt-1">
									<input type="checkbox" class="form-control" id="halfday" v-model="halfday"
										   :disabled="currentEventObject.approved">
									Half Day
								</label>
							</div>
						</div>
						<div class="form-group">
							<label for="comment">Comment</label>
							<input :disabled="adminMode === true || currentEventObject.approved" type="text" class="form-control" id="comment"
								   v-model="comment">
						</div>
						<div class="form-group" v-if="userRoles.includes('hr.create') && adminMode === false && currentEvent === null">
							<label for="user">For User</label>
							<select class="form-control" id="user" v-model="user" :disabled="currentEventObject.approved">
								<option value="null">For me</option>
								<option v-if="userRoles.includes('users.lineManager')" value="all">Everyone</option>
								<option :value="user.id" v-for="user in orderedAllUsers">{{ user.full_name }}</option>
							</select>
						</div>
						<div class="form-group" v-if="adminMode === true">
							<label for="admincomment">Admin Comment</label>
							<input type="text" class="form-control" id="admincomment" v-model="admincomment" :disabled="currentEventObject.approved">
						</div>
						<div class="form-group"
							 v-if="adminMode === false && currentEventObject.hasOwnProperty('id') && currentEventObject.approved !== null">
							<label for="admincomment">Admin Comment</label>
							<input type="text" class="form-control" id="admincomment" v-model="admincomment" disabled>
						</div>
					</div>
					<div class="five-lg text-center">
						<div class="flex box">
							<div class="six-lg">
								<h3 class="mt-0 mb-0">{{ workDays() }}</h3>
								<p>days long</p>
							</div>
							<div class="six-lg" v-if="user === null || user === currentUser.id">
								<h3 v-if="currentEvent === null" class="mt-0 mb-0">
									{{ remainingDayOffs }} ({{ currentUser.total_days_off }})
								</h3>
								<h3 v-if="currentEvent !== null" class="mt-0 mb-0" :class="remainingDayOffs >= 0 ?'':'text-danger'">
									{{ remainingDayOffs }} ({{ currentUser.total_days_off }})
								</h3>
								<p>days remaining (total)</p>
							</div>
							<div class="six-lg" v-if="user !== null && user !== currentUser.id && user !== 'all'">
								<h3 v-if="currentEvent === null" class="mt-0 mb-0">
									{{ currentEvent }}
									{{ allUsers[user].remainingDayOffs }} ({{ allUsers[user].total_days_off }})
								</h3>
								<h3 v-if="currentEvent !== null" class="mt-0 mb-0" :class="remainingDayOffs >= 0 ?'':'text-danger'">
									{{ allUsers[user].remainingDayOffs }} ({{ allUsers[user].total_days_off }})
								</h3>
								<p>days remaining (total)</p>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template slot="footer">
				<a v-if="adminMode === false && currentEvent === null" :disabled="remainingDayOffs < 0"
				   @click="remainingDayOffs >= 0 ? saveDayOff(null) : doNothing" class="btn btn-md btn-success"><i class="fas fa-check"></i> Send
					Reservation</a>
				<a v-if="adminMode === false && currentEvent !== null && currentEventObject.startIn > 1" @click="saveDayOff('selfdelete')"
				   class="btn btn-md btn-danger"><i class="fas fa-trash"></i> Delete Reservation</a>
				<a v-if="adminMode === false && currentEvent !== null && currentEventObject.startIn <= 1" @click="doNothing"
				   class="btn btn-md btn-danger disabled" title="Ongoing or past day-offs cannot be deleted"><i class="fas fa-trash"></i> Delete
					Reservation</a>
				<a v-if="adminMode === false && currentEvent !== null" :disabled="currentEventObject.approved === true || remainingDayOffs < 0"
				   @click="currentEventObject.approved === false && remainingDayOffs >= 0 ? saveDayOff(null) : doNothing"
				   title="Only unapproved reservation can be edited!" class="btn btn-md btn-success"><i class="fas fa-save"></i> Update
					Reservation</a>
				<a v-if="adminMode === true && currentEvent !== null && currentEventObject.approved !== true" @click="saveDayOff(false)"
				   class="btn btn-md btn-danger"><i class="fas fa-times"></i> Reject Reservation</a>
				<a v-if="adminMode === true && currentEvent !== null && currentEventObject.approved !== true" @click="saveDayOff(true)"
				   class="btn btn-md btn-success"><i class="fas fa-check"></i> Approve Reservation</a>
				<a v-if="adminMode === true && currentEvent !== null && currentEventObject.approved === true" @click="saveDayOff(false)"
				   class="btn btn-md btn-danger"><i class="fas fa-times"></i> Delete Reservation</a>
				<a v-if="adminMode === true && currentEvent !== null && currentEventObject.approved === true" @click="saveDayOff(true)"
				   class="btn btn-md btn-success"><i class="fas fa-check"></i> Update Reservation</a>
			</template>
		</modal>
	</div>
</template>

<script>
import modal from '../Modal/DefaultModal.vue';
import fullCalendar from 'vue-fullcalendar'

Vue.component('full-calendar', fullCalendar)

export default {
	components: {
		modal
	},
	data() {
		return {
			currentUser: this.$currentUser,
			userRoles: this.$currentUserRoles,
			isModalVisible: false,
			userDetails: {},

			startDate: '2000-01-01',
			endDate: '2000-01-01',

			events: [],
			currentEvent: null,
			currentEventObject: {},
			comment: '',

			pendingDayOffs: {},
			adminMode: false,
			admincomment: '',

			user: null,
			allUsers: {},

			halfday: false,
			currentHrUser: this.$currentUser,
			wasOff: {}
		}
	},
	mounted() {
		this.fetchHrData();
	},
	watch: {
		halfday: function (val) {
			if (this.halfday === true) {
				this.endDate = this.startDate;
			}
			return this.halfday;
		},
		endDate: function (val) {
			if (this.halfday === true) {
				this.startDate = this.endDate;
			}
			if (this.endDate < this.startDate) {
				this.startDate = this.endDate;
			}
			return this.endDate;
		},
		startDate: function (val) {
			if (this.halfday === true) {
				this.endDate = this.startDate;
			}
			if (this.startDate > this.endDate) {
				this.endDate = this.startDate;
			}
			return this.startDate;
		}

	},
	computed: {
		orderedAllUsers: function () {
			return _.orderBy(this.allUsers, ['full_name'], ['asc'])
		},
		remainingDayOffs: function () {
			if (this.userDetails === null || typeof this.userDetails === 'undefined') {
				return 0;
			}

			if (this.currentEvent === null) {
				return (this.userDetails.remainingDayOffs - this.workDays());
			}

			if (this.currentEventObject === null || typeof this.currentEventObject === 'undefined') {
				return 0;
			}
			this.currentEventObject.length = Math.round(parseFloat(this.currentEventObject.length) * 10) / 10;
			return (this.userDetails.remainingDayOffs + this.currentEventObject.length - this.workDays());
		},
		nextDayOff: function () {
			let nextDayOff = {
				id: 0
			};
			this.events.some(function (event) {
				if (event.isOwn === true && event.isPublicHoliday === false && event.hasOwnProperty('approved') && event.approved === true && event.hasOwnProperty('startIn') && event.hasOwnProperty('length') && (event.startIn >= 0 || Math.abs(event.startIn) < parseInt(event.length))) {
					nextDayOff = event;
					return true;
				}
			});
			return nextDayOff;
		}
	},
	methods: {
		fetchHrData: function () {
			let vm = this;
			axios.get('/api/hr/user/' + this.currentUser.id
			).then(function (response) {
				vm.events = response.data.events;
				vm.userDetails = response.data.user;
				vm.pendingDayOffs = response.data.pendingDayOffs;
				vm.allUsers = response.data.allUsers;
				vm.wasOff = response.data.wasOff
				vm.closeModal();
			});
		},
		dayClick: function (day) {
			this.currentEvent = null;
			this.comment = '';
			this.admincomment = '';
			this.currentEventObject = {
				approved: false,
			};
			if (day === null) {
				day = new Date();
			}
			this.startDate = this.formatDate(day);
			this.endDate = this.formatDate(day);
			this.isModalVisible = true;
		},
		closeModal: function () {
			this.isModalVisible = false;
			this.currentEvent = null;
			this.currentEventObject = {
				approved: false
			};
			this.comment = '';
			this.admincomment = '';
			this.user = null;
			this.halfday = false;
			this.adminMode = false;
		},
		formatDate(date) {
			date = new Date(date);
			return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
		},
		workDays: function () {
			const startDate = this.parseDate(this.startDate);
			const endDate = this.parseDate(this.endDate);

			if (startDate.getUTCDate() === endDate.getUTCDate() && this.halfday === false) {
				return 1;
			}

			if (startDate.getUTCDate() === endDate.getUTCDate() && this.halfday === true) {
				return 0.5;
			}

			if (endDate.getUTCDate() < startDate.getUTCDate()) {
				return this.calcBusinessDays(startDate, endDate);
			} // if

			let currentDate = startDate;
			let i = 0;
			let length = 0;
			while (currentDate <= endDate) {
				if (currentDate.getDay() < 6 && currentDate.getDay() > 0 && this.isItPublicHoliday(this.formatDate(currentDate)) !== true) {
					length++;
				}
				if ((currentDate.getDay() >= 6 || currentDate.getDay() === 0) && this.isItExtraWorkDay(this.formatDate(currentDate)) === true) {
					length++;
				}
				i++;
				currentDate.setDate(startDate.getDate() + 1);
			}
			if (this.halfday === true) {
				length = length - 0.5;
			}
			return length;
		},
		calcBusinessDays(dDate1, dDate2) { // input given as Date objects
			var iWeeks, iDateDiff, iAdjust = 0;
			if (dDate2 < dDate1) return -1; // error code if dates transposed
			var iWeekday1 = dDate1.getDay(); // day of week
			var iWeekday2 = dDate2.getDay();
			iWeekday1 = (iWeekday1 === 0) ? 7 : iWeekday1; // change Sunday from 0 to 7
			iWeekday2 = (iWeekday2 === 0) ? 7 : iWeekday2;
			if ((iWeekday1 > 5) && (iWeekday2 > 5)) iAdjust = 1; // adjustment if both days on weekend
			iWeekday1 = (iWeekday1 > 5) ? 5 : iWeekday1; // only count weekdays
			iWeekday2 = (iWeekday2 > 5) ? 5 : iWeekday2;

			// calculate differnece in weeks (1000mS * 60sec * 60min * 24hrs * 7 days = 604800000)
			iWeeks = Math.floor((dDate2.getTime() - dDate1.getTime()) / 604800000)

			if (iWeekday1 < iWeekday2) { //Equal to makes it reduce 5 days
				iDateDiff = (iWeeks * 5) + (iWeekday2 - iWeekday1)
			} else {
				iDateDiff = ((iWeeks + 1) * 5) - (iWeekday1 - iWeekday2)
			}

			iDateDiff -= iAdjust // take into account both days on weekend

			return (iDateDiff + 1); // add 1 because dates are inclusive
		},
		parseDate: function (date) {
			let parts = date.split('-');
			return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
		},
		isItPublicHoliday: function (date) {
			let check = false;
			this.events.some(function (event) {
				if (event.isPublicHoliday === true && event.date === date && event.title !== 'Extra Work Day') {
					check = true;
					return true;
				}
			});
			return check;
		},
		isItExtraWorkDay: function (date) {
			let check = false;
			this.events.some(function (event) {
				if (event.isPublicHoliday === true && event.date === date && event.title === 'Extra Work Day') {
					check = true;
					return true;
				}
			});
			return check;
		},
		saveDayOff: function (approved = null) {
			let vm = this;
			let url = '/api/hr/user/' + this.currentUser.id + '/dayoff';

			if (this.currentEvent !== null) {
				url += '/' + this.currentEvent;
			} // if

			if (approved === 'selfdelete') {
				approved = false;
				this.admincomment = 'selfdelete';
			} // if

			axios.post(url,
					{
						'start_date': vm.startDate,
						'end_date': vm.endDate,
						'length': vm.workDays(),
						'comment': vm.comment,
						'admincomment': vm.admincomment,
						'approved': approved,
						'user': vm.user,
					}
			).then(function (response) {
				vm.fetchHrData();
			});
		},
		eventClick: function (event, jsEvent, pos) {
			if (event.isOwn !== true && !this.userRoles.includes('hr.create')) {
				alert('You can only edit your own day off!');
				return true;
			}
			if (event.isPublicHoliday === true) {
				alert('Public Holiday: ' + event.title);
				return true;
			}

			this.resetEvent(event);
			if (this.userRoles.includes('hr.create')) {
				if (event.hasOwnProperty('approved') && event.approved !== true) {
					event = this.pendingDayOffs[event.id];
				}
				this.adminEvent(event);

			}
			this.isModalVisible = true;
		},
		adminEvent: function (event) {
			this.resetEvent(event);
			this.startDate = event.start_date;
			this.endDate = event.end_date;
			this.adminMode = true;
			this.user = event.user_id;
			this.isModalVisible = true;
		},
		resetEvent: function (event) {
			this.currentEvent = event.id;
			this.startDate = event.start;
			this.endDate = event.end;
			this.comment = event.comment;
			this.currentEventObject = event;
			this.admincomment = event.admincomment;
			if (this.currentEventObject !== null && this.currentEventObject.hasOwnProperty('halfday')) {
				this.halfday = this.currentEventObject.halfday;
			}
			if (this.currentEventObject !== null && this.currentEventObject.hasOwnProperty('length')) {
				this.halfday = String(this.currentEventObject.length) === '0.5';
			}
			this.adminMode = false;
		},
		doNothing: function () {
			return true;
		}
	}
}
</script>

<style>
.comp-full-calendar {
	max-width: inherit;
	padding: 0;
	background: rgba(0, 0, 0, 0);
}

.events-day:nth-child(6) {
	background: rgba(120, 120, 120, 0.2);
}

.events-day:nth-child(7) {
	background: rgba(120, 120, 120, 0.2);
}

.event-item.public {
	background: rgba(0, 255, 0, 0.3) !important;
}

.event-item.own.rejected {
	background: rgba(255, 0, 0, 0.3) !important;
}

.event-item.own.pending {
	background: rgba(247, 202, 24, 1) !important;
}

.event-item.notown.pending {
	background: rgba(247, 202, 24, 1) !important;
}

.event-item.own.approved {
	background: rgba(0, 255, 0, 0.6) !important;
}

.event-item.notown {
	background: rgba(0, 0, 255, 0.3) !important;
}

.full-calendar-body .dates .dates-events .events-week .events-day {
	min-height: 100px !important;
}

p.event-item.halfday:before {
	content: 'Half Day -';
}

.full-calendar-body .weeks .week {
	padding: 8px;
	color: #FFF;
	background-color: #405580;
}

.full-calendar-header .header-center .prev-month,
.full-calendar-header .header-center .next-month {
	padding: 10px 16px;
}

.full-calendar-header .header-center .prev-month:hover,
.full-calendar-header .header-center .next-month:hover {
	color: #2EA8E5;
}

.full-calendar-header .header-center {
	font-family: "Bold", system;
	font-size: 26px;
	letter-spacing: -1.25px;
}
</style>

